'use client'

import Link from 'next/link'
import { ArrowRight, FileText } from 'lucide-react'

function trackCta(cta: string) {
  try {
    fetch('/api/analytics/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_name: 'marketing_cta_clicked', properties: { location: 'final_cta', cta } }),
    }).catch(() => {})
  } catch { /* ignore */ }
}

export function MarketingFinalCta() {
  return (
    <section className="relative overflow-hidden bg-[#0b1520] py-12 sm:py-24">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_50%_50%,rgba(30,58,95,0.55),transparent)]" />
        <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#c2571a]/5 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <p className="mb-4 text-xs font-semibold uppercase text-[#c7681f]">Start free</p>
        <h2 className="mb-4 text-[2rem] font-bold leading-[1.1] text-white sm:mb-5 sm:text-[2.8rem]">
          Start with one job.
          <br />
          <span className="text-white/62 sm:text-white/45">Build from there.</span>
        </h2>
        <p className="mx-auto mb-7 max-w-xl text-[1rem] leading-relaxed text-white/74 sm:mb-9 sm:text-[1.1rem] sm:text-white/50">
          Start free, no credit card required. Set up one job, send one quote, capture one receipt — and see how much lighter the admin feels. Then build from there.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5">
          <a
            href="/signup"
            className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#c2571a] px-8 py-4 text-[15px] font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-[#a84915] active:translate-y-0 sm:w-auto"
            onClick={() => trackCta('start_free')}
          >
            Start Free
            <ArrowRight size={16} strokeWidth={2} aria-hidden />
          </a>
          <Link
            href="/#workflow"
            className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-8 py-4 text-[15px] font-semibold text-white/82 transition-all hover:bg-white/10 hover:text-white sm:w-auto"
            onClick={() => trackCta('see_how_it_works')}
          >
            <FileText size={15} strokeWidth={2} aria-hidden />
            See How It Works
          </Link>
        </div>

        <p className="mt-4 text-xs font-semibold uppercase text-white/58 sm:text-white/35">No card required · Start with one job · Cancel any time</p>

        <p className="mt-3 text-[13px] leading-6 text-white/55 sm:text-white/40">
          Already signed up?{' '}
          <Link
            href="/install-app"
            className="font-semibold text-white/85 underline decoration-white/30 underline-offset-2 hover:decoration-white/70"
            onClick={() => trackCta('install_app_from_final_cta')}
          >
            Install BuilderBuddi as an app
          </Link>{' '}
          on your Mac or Android — same login, same data, opens like a real app.
        </p>

        <div className="mt-8 grid gap-2 text-left sm:mt-9 sm:flex sm:flex-wrap sm:justify-center sm:gap-5 sm:text-center">
          {[
            'Professional PDF quotes',
            'Online client approvals',
            'Automated payment reminders',
            'Works on mobile and desktop',
          ].map((item) => (
            <span key={item} className="flex items-center gap-1.5 text-sm font-medium text-white/64 sm:text-white/35">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M2 6.5l3 3 6-6" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}
